import { ReactNode } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EmailIcon from '@mui/icons-material/Email';
import TextField from '@mui/material/TextField';
import { TextSpan } from '../../Atoms/Text';
import { ScheduleSetting } from '../../../store/ScheduleSettings';
import { SchedulePausedChip } from './SchedulePausedChip';
import { viewKeysOf } from './scheduleTabs';

export interface OwnViewRowProps {
  viewName: string;
  schedule: ScheduleSetting | null;
  ownSchedule: ScheduleSetting | null;
  holders: ScheduleSetting[];
  onRunSchedule: (id: string) => void;
  onToggleScheduler: () => void;
  // What manages the view itself; a further report that starts with a view shown above it has none of these.
  viewActions?: {
    isEditing: boolean;
    editedName: string;
    blockedReason?: string;
    onEditedNameChange: (name: string) => void;
    onSaveName: () => void;
    onEdit: () => void;
    onDelete: () => void;
  };
  children?: ReactNode;
}

export const OwnViewRow = ({
  viewName,
  schedule,
  ownSchedule,
  holders,
  onRunSchedule,
  onToggleScheduler,
  viewActions,
  children
}: OwnViewRowProps) => {
  const tabCount = ownSchedule ? viewKeysOf(ownSchedule).length : 1;

  return (
    <div className="border-b mx-4">
      <div className="flex py-3">
        {viewActions?.isEditing ? (
          <div className="flex grow space-x-3">
            <TextField
              required
              margin="dense"
              id="name"
              name="name"
              label="View Name"
              value={viewActions.editedName}
              onChange={(e) => viewActions.onEditedNameChange(e.target.value)}
              fullWidth
              variant="outlined"
            />
            <IconButton
              sx={{ color: 'rgb(107 114 128)' }}
              size="small"
              aria-label="Save view name"
              onClick={viewActions.onSaveName}>
              <DoneIcon />
            </IconButton>
          </div>
        ) : (
          <>
            <span className="self-center">{viewName}</span>
            {ownSchedule && ownSchedule.name !== viewName && (
              <TextSpan
                size="xs"
                color="grey5"
                className="self-center ml-2">
                Report: {ownSchedule.name}
              </TextSpan>
            )}
            {tabCount > 1 && (
              <TextSpan
                size="xs"
                color="grey5"
                className="self-center ml-2">
                {tabCount} tabs
              </TextSpan>
            )}
            {holders.length > 0 && (
              <TextSpan
                size="xs"
                color="grey5"
                className="self-center ml-2">
                Tab of {holders.map((report) => report.name).join(', ')}
              </TextSpan>
            )}
            {ownSchedule?.paused_at && (
              <span className="self-center ml-2">
                <SchedulePausedChip reason={ownSchedule.paused_reason} />
              </span>
            )}
            <div className="ml-auto space-x-0">
              {(schedule || holders.length === 0) && (
                <>
                  <Tooltip title={schedule ? 'Send now to all recipients' : 'No schedule set'}>
                    <span>
                      <IconButton
                        sx={{ color: schedule ? 'rgb(107 114 128)' : 'rgb(209 213 219)' }}
                        size="small"
                        aria-label="Send now"
                        disabled={!schedule}
                        onClick={() => schedule && onRunSchedule(schedule.id)}>
                        <EmailIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={schedule ? 'Edit Schedule' : 'Add Schedule'}>
                    <IconButton
                      sx={{ color: schedule ? 'rgb(79 70 229)' : 'rgb(107 114 128)' }}
                      size="small"
                      aria-label={schedule ? 'Edit schedule' : 'Add schedule'}
                      onClick={onToggleScheduler}>
                      <CalendarMonthIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {viewActions && (
                <>
                  <Tooltip title={viewActions.blockedReason ?? 'Rename view'}>
                    <span>
                      <IconButton
                        sx={{ color: 'rgb(107 114 128)' }}
                        size="small"
                        aria-label="Rename view"
                        disabled={!!viewActions.blockedReason}
                        onClick={viewActions.onEdit}>
                        <EditIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={viewActions.blockedReason ?? 'Delete view'}>
                    <span>
                      <IconButton
                        sx={{ color: 'rgb(107 114 128)' }}
                        size="small"
                        aria-label="Delete view"
                        disabled={!!viewActions.blockedReason}
                        onClick={viewActions.onDelete}>
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
            </div>
          </>
        )}
      </div>
      {children}
    </div>
  );
};
